import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Check, ArrowRight, ArrowLeft, LogOut, Sparkles, Plus,
  Building2, MapPin, Globe2, Target, Database, ClipboardCheck,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { CountrySelect } from "@/components/CountrySelect";
import { Logo } from "@/components/Logo";
import { objectiveLabel } from "@/lib/profileOptions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ── Reference data ─────────────────────────────────────────────────────────
const ORG_TYPES = [
  "Destination / Tourism Board",
  "Hotel",
  "Lodge",
  "Hotel/Lodge Collection",
  "DMC",
  "Airline",
  "Transport Company",
  "Travel Agency / Tour Operator",
  "Tourism Activity",
  "Other Tourism Partner",
];

// Territory represented by the organization (what YOU manage / operate).
const GEO_LEVELS = [
  { value: "GLOBAL", label: "International / Mondial", hint: "Groupe présent dans plusieurs pays" },
  { value: "CONTINENT", label: "Continent", hint: "Ex : Europe, Afrique" },
  { value: "COUNTRY", label: "Pays", hint: "Ex : France, Kenya" },
  { value: "REGION", label: "Région", hint: "Ex : Toscane, Savoie Mont-Blanc" },
  { value: "DESTINATION", label: "Destination touristique", hint: "Ex : Côte d'Azur" },
  { value: "CITY", label: "Ville", hint: "Ex : Nice, New York" },
  { value: "NEIGHBORHOOD", label: "Quartier / zone locale", hint: "Ex : Vieux-Nice" },
  { value: "CUSTOM", label: "Territoire personnalisé", hint: "Périmètre sur mesure" },
];

// Priority source markets ANALYZED (emitting markets — where visitors come from).
const SUGGESTED_MARKETS = [
  "États-Unis", "Royaume-Uni", "Allemagne", "France", "Émirats A.U.",
  "Brésil", "Japon", "Inde", "Chine", "Canada", "Australie", "Italie", "Espagne",
];

const OBJECTIVES = [
  "Increase revenue",
  "Develop low season",
  "Find new source markets",
  "Improve occupancy",
  "Develop distribution",
  "Activate partners",
  "Improve visitor dispersion",
  "Measure performance",
];

const DATA_READINESS = [
  { value: "will_import", label: "Oui, je les importerai" },
  { value: "api", label: "Oui, via une API / connecteur" },
  { value: "none", label: "Non, pas encore" },
  { value: "unsure", label: "Je ne sais pas encore" },
];

const STEPS = [
  { key: "organisation", label: "Organisation", icon: Building2 },
  { key: "localisation", label: "Localisation", icon: MapPin },
  { key: "territoire", label: "Territoire", icon: Globe2 },
  { key: "marches", label: "Marchés", icon: Target },
  { key: "objectifs", label: "Objectifs & Données", icon: Database },
  { key: "synthese", label: "Synthèse", icon: ClipboardCheck },
];

// ── Small UI atoms ───────────────────────────────────────────────────────────
const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
      active
        ? "border-[#1E3D2B] bg-[#1E3D2B] text-white"
        : "border-slate-200 bg-white text-slate-600 hover:border-[#CBD5E1]"
    }`}
  >
    {active && <Check size={13} className="mr-1.5 inline" />}
    {children}
  </button>
);

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    {children}
    {hint && <p className="text-xs text-slate-400">{hint}</p>}
  </div>
);

const SummaryRow = ({ label, value, testid }) => (
  <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-3 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="max-w-[60%] text-right text-sm font-medium text-[#1E3D2B]" data-testid={testid}>
      {value || <span className="font-normal text-slate-400">Non renseigné</span>}
    </span>
  </div>
);

// ── Page ───────────────────────────────────────────────────────────────────
export default function OrganizationSetup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { activeMembership, activeOrgId, logout, reload } = useAuth();
  const orgId = params.get("org") || activeOrgId;

  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [customMarket, setCustomMarket] = useState("");

  const [form, setForm] = useState({
    type: activeMembership?.org.type || "",
    country: "",
    country_code: "",
    geographic_level: "",
    geographic_entity: "",
    source_markets: [],
    objectives: [],
    data_readiness: "",
  });

  // Resume: prefill from any data already saved on the organization.
  const org = activeMembership?.org;
  useEffect(() => {
    if (!org) return;
    setForm((f) => ({
      type: org.type || f.type,
      country: org.country || f.country,
      country_code: org.country_code || f.country_code,
      geographic_level: org.geographic_level || f.geographic_level,
      geographic_entity: org.geographic_entity || f.geographic_entity,
      source_markets: org.source_markets?.length ? org.source_markets : f.source_markets,
      objectives: org.objectives?.length ? org.objectives : f.objectives,
      data_readiness: org.data_readiness || f.data_readiness,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org?.id]);

  const toggle = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));

  const addCustomMarket = () => {
    const v = customMarket.trim();
    if (v && !form.source_markets.includes(v)) {
      setForm((f) => ({ ...f, source_markets: [...f.source_markets, v] }));
    }
    setCustomMarket("");
  };

  const canNext = () => {
    if (step === 0) return !!form.type;
    if (step === 1) return !!form.country.trim();
    if (step === 2) {
      // Territory optional overall, but if a level is chosen the entity must be filled.
      if (form.geographic_level && !form.geographic_entity.trim()) return false;
      return true;
    }
    return true;
  };

  const submit = async () => {
    setError("");
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        source_markets: form.source_markets,
        objectives: form.objectives,
      };
      if (form.country.trim()) payload.country = form.country.trim();
      if (form.country_code.trim()) payload.country_code = form.country_code.trim();
      // Territory: only send when both level + entity are provided (never empty strings).
      if (form.geographic_level && form.geographic_entity.trim()) {
        payload.geographic_level = form.geographic_level;
        payload.geographic_entity = form.geographic_entity.trim();
      }
      if (form.data_readiness) payload.data_readiness = form.data_readiness;
      await api.patch(`/organizations/${orgId}/profile`, payload);
      await reload();
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err.message));
    } finally {
      setSaving(false);
    }
  };

  const levelLabel = GEO_LEVELS.find((l) => l.value === form.geographic_level)?.label;
  const readinessLabel = DATA_READINESS.find((r) => r.value === form.data_readiness)?.label;

  // ── Success screen (French) ─────────────────────────────────────────────
  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAFAF6] px-6" data-testid="setup-success">
        <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#5C9873] to-[#3E7256] shadow-lg">
            <Sparkles size={28} className="text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-[#1E3D2B]">
            Votre espace Lodge &amp; Co est prêt
          </h1>
          <p className="mx-auto mt-3 max-w-sm text-sm text-slate-500">
            Votre espace <span className="font-semibold text-slate-700">{activeMembership?.org.name}</span> est
            configuré. Vous pouvez maintenant explorer vos données et opportunités.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            data-testid="setup-enter-workspace"
            className="mt-8 inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#1E3D2B] px-8 text-sm font-semibold text-white transition-colors hover:bg-[#2C5540]"
          >
            Accéder à mon espace <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  const isLast = step === STEPS.length - 1;

  return (
    <div className="min-h-screen bg-[#FAFAF6]">
      <header className="flex h-16 items-center justify-between border-b border-slate-200 bg-white px-6">
        <div className="flex items-center gap-2.5">
          <Logo className="h-12" />
          <span className="font-heading text-base font-bold text-[#1E3D2B]">Lodge &amp; Co</span>
        </div>
        <button
          onClick={logout}
          data-testid="setup-logout"
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#1E3D2B]"
        >
          <LogOut size={15} /> Déconnexion
        </button>
      </header>

      <div className="mx-auto max-w-2xl px-6 py-12" data-testid="org-setup">
        {/* Progress */}
        <div className="mb-10">
          <div className="mb-3 flex items-center justify-between gap-1">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.key} className="flex items-center gap-2">
                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                      i < step
                        ? "bg-[#059669] text-white"
                        : i === step
                        ? "bg-[#1E3D2B] text-white"
                        : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    {i < step ? <Check size={15} /> : <Icon size={15} />}
                  </span>
                  <span className={`hidden text-xs font-medium lg:inline ${i === step ? "text-[#1E3D2B]" : "text-slate-400"}`}>
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-[#3E7256] transition-all duration-300"
              style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
            />
          </div>
          <p className="mt-2 text-xs font-medium text-slate-400" data-testid="setup-step-indicator">
            Étape {step + 1} sur {STEPS.length} — {STEPS[step].label}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-8">
          {error && (
            <div className="mb-5 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700" data-testid="setup-error">
              {error}
            </div>
          )}

          {/* Step 1 — Organisation */}
          {step === 0 && (
            <div className="space-y-6" data-testid="step-organisation">
              <div>
                <h2 className="font-heading text-xl font-bold text-[#1E3D2B]">Votre organisation</h2>
                <p className="mt-1 text-sm text-slate-500">Quel type d'acteur du tourisme représentez-vous ?</p>
              </div>
              <Field label="Type d'organisation">
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="setup-type-trigger" className="h-11">
                    <SelectValue placeholder="Sélectionnez un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ORG_TYPES.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`setup-type-${t}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          )}

          {/* Step 2 — Localisation */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-localisation">
              <div>
                <h2 className="font-heading text-xl font-bold text-[#1E3D2B]">Localisation</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Dans quel pays votre organisation est-elle basée ? Le code pays ISO est capturé automatiquement.
                </p>
              </div>
              <Field label="Pays">
                <CountrySelect
                  value={form.country}
                  onChange={(name, code) =>
                    setForm((f) => ({ ...f, country: name, country_code: code || "" }))
                  }
                  testid="setup-country"
                />
              </Field>
              {form.country_code && (
                <div className="inline-flex items-center gap-2 rounded-lg border border-[#D6E5D6] bg-[#ECF3EC] px-3 py-2 text-sm text-[#1E3D2B]" data-testid="setup-country-code">
                  Code pays ISO&nbsp;: <span className="font-semibold">{form.country_code}</span>
                </div>
              )}
            </div>
          )}

          {/* Step 3 — Territoire */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-territoire">
              <div>
                <h2 className="font-heading text-xl font-bold text-[#1E3D2B]">Territoire représenté</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Quel périmètre géographique gérez-vous ? C'est <span className="font-semibold text-slate-700">votre territoire</span>,
                  distinct des marchés que vous analysez. Optionnel — vous pourrez l'ajuster plus tard.
                </p>
              </div>
              <Field label="Niveau du territoire">
                <select
                  value={form.geographic_level}
                  onChange={(e) => setForm((f) => ({ ...f, geographic_level: e.target.value }))}
                  data-testid="setup-geo-level"
                  className="h-11 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#5C9873] focus:ring-2 focus:ring-[#5C9873]/15"
                >
                  <option value="">— Non défini —</option>
                  {GEO_LEVELS.map((l) => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </select>
              </Field>
              {form.geographic_level && (
                <Field
                  label="Entité géographique"
                  hint={GEO_LEVELS.find((l) => l.value === form.geographic_level)?.hint}
                >
                  <input
                    value={form.geographic_entity}
                    onChange={(e) => setForm((f) => ({ ...f, geographic_entity: e.target.value }))}
                    data-testid="setup-geo-entity"
                    placeholder="Ex : France, Nice, Toscane…"
                    className="h-11 w-full rounded-lg border border-slate-200 px-3.5 text-sm outline-none focus:border-[#5C9873] focus:ring-2 focus:ring-[#5C9873]/15"
                  />
                </Field>
              )}
            </div>
          )}

          {/* Step 4 — Marchés */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-markets">
              <div>
                <h2 className="font-heading text-xl font-bold text-[#1E3D2B]">Marchés prioritaires analysés</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Quels <span className="font-semibold text-slate-700">marchés émetteurs</span> souhaitez-vous analyser
                  (d'où viennent vos visiteurs) ? Plusieurs choix possibles.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {[...new Set([...SUGGESTED_MARKETS, ...form.source_markets])].map((m) => (
                  <Chip
                    key={m}
                    active={form.source_markets.includes(m)}
                    onClick={() => toggle("source_markets", m)}
                    testid={`market-chip-${m}`}
                  >
                    {m}
                  </Chip>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <input
                  value={customMarket}
                  onChange={(e) => setCustomMarket(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomMarket())}
                  data-testid="setup-custom-market"
                  placeholder="Ajouter un autre marché…"
                  className="h-11 min-w-0 flex-1 rounded-lg border border-slate-200 px-3.5 text-sm outline-none focus:border-[#5C9873] focus:ring-2 focus:ring-[#5C9873]/15"
                />
                <button
                  type="button"
                  onClick={addCustomMarket}
                  data-testid="setup-add-market"
                  className="flex h-11 shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                >
                  <Plus size={16} /> Ajouter
                </button>
              </div>
            </div>
          )}

          {/* Step 5 — Objectifs & Données */}
          {step === 4 && (
            <div className="space-y-7" data-testid="step-objectives">
              <div>
                <h2 className="font-heading text-xl font-bold text-[#1E3D2B]">Vos objectifs</h2>
                <p className="mt-1 text-sm text-slate-500">Sélectionnez vos priorités (plusieurs choix possibles).</p>
              </div>
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {OBJECTIVES.map((o) => {
                  const active = form.objectives.includes(o);
                  return (
                    <button
                      key={o}
                      type="button"
                      data-testid={`objective-chip-${o}`}
                      onClick={() => toggle("objectives", o)}
                      className={`flex items-center justify-between rounded-xl border px-4 py-3.5 text-left text-sm font-medium transition-all ${
                        active
                          ? "border-[#3E7256] bg-[#ECF3EC] text-[#1E3D2B]"
                          : "border-slate-200 bg-white text-slate-600 hover:border-[#CBD5E1]"
                      }`}
                    >
                      {objectiveLabel(o)}
                      <span
                        className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                          active ? "border-[#3E7256] bg-[#3E7256]" : "border-slate-300"
                        }`}
                      >
                        {active && <Check size={12} className="text-white" strokeWidth={3} />}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="border-t border-slate-100 pt-6">
                <Field label="Disposez-vous déjà de données à analyser ?">
                  <select
                    value={form.data_readiness}
                    onChange={(e) => setForm((f) => ({ ...f, data_readiness: e.target.value }))}
                    data-testid="setup-data-readiness"
                    className="h-11 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#5C9873] focus:ring-2 focus:ring-[#5C9873]/15"
                  >
                    <option value="">— Optionnel —</option>
                    {DATA_READINESS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>
          )}

          {/* Step 6 — Synthèse */}
          {step === 5 && (
            <div className="space-y-6" data-testid="step-synthese">
              <div>
                <h2 className="font-heading text-xl font-bold text-[#1E3D2B]">Synthèse</h2>
                <p className="mt-1 text-sm text-slate-500">Vérifiez vos informations avant de créer votre espace.</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-[#FBFCFB] px-5 py-2">
                <SummaryRow label="Type d'organisation" value={form.type} testid="summary-type" />
                <SummaryRow
                  label="Pays"
                  value={form.country ? `${form.country}${form.country_code ? ` (${form.country_code})` : ""}` : ""}
                  testid="summary-country"
                />
                <SummaryRow
                  label="Territoire représenté"
                  value={form.geographic_level ? `${levelLabel} — ${form.geographic_entity}` : ""}
                  testid="summary-territory"
                />
                <SummaryRow
                  label="Marchés analysés"
                  value={form.source_markets.length ? form.source_markets.join(", ") : ""}
                  testid="summary-markets"
                />
                <SummaryRow
                  label="Objectifs"
                  value={form.objectives.length ? form.objectives.map(objectiveLabel).join(", ") : ""}
                  testid="summary-objectives"
                />
                <SummaryRow label="Données disponibles" value={readinessLabel} testid="summary-readiness" />
              </div>
              <p className="text-xs text-slate-400">
                Le <span className="font-medium text-slate-500">territoire représenté</span> (ce que vous gérez) reste
                distinct des <span className="font-medium text-slate-500">marchés analysés</span> (d'où viennent vos visiteurs).
              </p>
            </div>
          )}

          {/* Nav */}
          <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              data-testid="setup-back"
              className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#1E3D2B] disabled:invisible"
            >
              <ArrowLeft size={15} /> Retour
            </button>
            {!isLast ? (
              <button
                type="button"
                onClick={() => canNext() && setStep((s) => s + 1)}
                disabled={!canNext()}
                data-testid="setup-next"
                className="flex h-11 items-center gap-2 rounded-lg bg-[#1E3D2B] px-6 text-sm font-semibold text-white hover:bg-[#2C5540] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Continuer <ArrowRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={saving}
                data-testid="setup-finish"
                className="flex h-11 items-center gap-2 rounded-lg bg-[#3E7256] px-6 text-sm font-semibold text-white hover:bg-[#2F5A43] disabled:opacity-60"
              >
                {saving ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                ) : (
                  <>Créer mon espace <Check size={16} /></>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
